import {
  EMRdetailsProps,
  EMRReferralKey,
  IEMRServiceReferral,
  IEMRUpdatePayloadProps,
} from "@/api_framework/api_modals/EMRs";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { DIAGNOSTICS_SERVICE_TYPE_ID } from "@/app/utils/bookingServiceSelection";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

// Order + labels shown to the doctor, and the service type each option is referred to.
// Dialysis has no service type of its own — the backend resolves it from "Dialysis".
// legacyServiceTypeIds: ids older EMRs stored for the option, still matched when reading them back.
export const EMR_REFERRAL_OPTIONS: {
  key: EMRReferralKey;
  label: string;
  serviceTypeId: string;
  serviceType: string;
  legacyServiceTypeIds?: string[];
}[] = [
  { key: "ipd", label: "IPD", serviceTypeId: "6881fa1c-9146-45aa-8ad1-5d5c3c8d5301", serviceType: "IPD" },
  {
    key: "ultrasound",
    label: "Ultrasound",
    serviceTypeId: DIAGNOSTICS_SERVICE_TYPE_ID,
    serviceType: "Ultrasound",
    legacyServiceTypeIds: ["1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed"],
  },
  { key: "surgery", label: "Surgery", serviceTypeId: "fb562ef3-4f87-4d0c-be7c-6925ccc431da", serviceType: "Surgery" },
  { key: "physio", label: "Physiotherapy", serviceTypeId: "bf1f6268-3d23-4134-9c3c-0c69c1e691a9", serviceType: "Physiotherapy" },
  { key: "dialysis", label: "Dialysis", serviceTypeId: "Dialysis", serviceType: "Dialysis" },
];

const findReferralOption = (referral?: IEMRServiceReferral) =>
  EMR_REFERRAL_OPTIONS.find(
    (option) =>
      (!!referral?.service_type_id &&
        (option.serviceTypeId === referral.service_type_id ||
          !!option.legacyServiceTypeIds?.includes(referral.service_type_id))) ||
      option.serviceType.toLowerCase() === referral?.service_type?.trim().toLowerCase(),
  );

type ReferralState = Record<EMRReferralKey, { referred: boolean; notes: string }>;

const emptyState = (): ReferralState =>
  EMR_REFERRAL_OPTIONS.reduce((acc, option) => {
    acc[option.key] = { referred: false, notes: "" };
    return acc;
  }, {} as ReferralState);

const stateFromReferrals = (referrals?: IEMRServiceReferral[]): ReferralState => {
  const state = emptyState();
  (referrals ?? []).forEach((referral) => {
    const option = findReferralOption(referral);
    if (option) state[option.key] = { referred: true, notes: referral?.notes ?? "" };
  });
  return state;
};

const LEGACY_NEXT_STEPS_FIELDS = [
  "suggested_services",
  "surgery_name",
  "surgery_date",
  "refer_city_id",
  "refer_clinic_id",
  "refer_staff_id",
  "refer_staff_name",
  "refer_to_reason",
  "refer_city_name",
  "refer_clinic_name",
] as const;

// EMRs filled with the old Next Steps UI keep showing it; "Refer to other doctor/clinic" only when these are all empty
export const hasLegacyNextSteps = (nextSteps?: IEMRUpdatePayloadProps["next_steps"]) =>
  LEGACY_NEXT_STEPS_FIELDS.some((field) => {
    const value = nextSteps?.[field];
    if (Array.isArray(value)) return value.length > 0;
    return value != null && String(value).trim() !== "";
  });

const ReferToOtherClinicComponent = ({ emrDetails }: EMRdetailsProps) => {
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const [referralState, setReferralState] = useState<ReferralState>(emptyState);

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const debouncedUpdate = useMemo(
    () =>
      debounce((state: ReferralState) => {
        const payload: IEMRUpdatePayloadProps = {
          service_referrals: EMR_REFERRAL_OPTIONS.filter((option) => state[option.key].referred).map((option) => ({
            service_type_id: option.serviceTypeId,
            service_type: option.serviceType,
            notes: state[option.key].notes,
          })),
          emr_status: emrDetails?.emr_status ?? emr_status,
        };
        updateEmrAPI(emr_id, payload);
      }, 1000),
    [emr_id, emrDetails?.emr_status],
  );

  const isFirstLoad = useRef(false);

  useEffect(() => {
    if (emrDetails) {
      isFirstLoad.current = false;
      setReferralState(stateFromReferrals(emrDetails?.service_referrals));
      setTimeout(() => {
        isFirstLoad.current = true;
      }, 0);
    }
  }, [emrDetails]);

  useEffect(() => () => debouncedUpdate.flush(), [debouncedUpdate]);

  const handleChange = (key: EMRReferralKey, change: Partial<{ referred: boolean; notes: string }>) => {
    setReferralState((prev) => {
      const next = { ...prev, [key]: { ...prev[key], ...change } };
      if (isFirstLoad.current) debouncedUpdate(next);
      return next;
    });
  };

  return (
    <div id="refer-section" className="w-full bg-white p-3 rounded-[10px] mt-3">
      <h3 className="text-[#656565] text-sm font-medium tracking-tight42 px-2">Refer to other doctor/clinic</h3>
      <div className="bg-white border border-[#E2E2E2] rounded-xl mt-2 overflow-hidden">
        {EMR_REFERRAL_OPTIONS.map((option) => {
          const { referred, notes } = referralState[option.key];
          return (
            <div key={option.key} className={`px-3 py-2 ${referred ? "bg-[#F9FAFB]" : "bg-white"}`}>
              <div className="flex items-start gap-3">
                <label className="flex items-center gap-2.5 w-36 shrink-0 pt-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={referred}
                    onChange={(e) => handleChange(option.key, { referred: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-black cursor-pointer focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
                  />
                  <span className="text-[#000000] text-sm font-medium tracking-tight42">{option.label}</span>
                </label>
                <textarea
                  rows={2}
                  value={notes}
                  // typing a note selects the referral
                  onChange={(e) =>
                    handleChange(option.key, e.target.value.trim() ? { notes: e.target.value, referred: true } : { notes: e.target.value })
                  }
                  className={`flex-1 min-w-0 border rounded-lg px-3 py-2 text-sm resize-y bg-white text-black placeholder-[#9CA3AF] focus:outline-none focus:border-black transition-colors border-[#E2E2E2]`}
                  placeholder="Add note"
                ></textarea>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReferToOtherClinicComponent;
